import {Send, ThumbDown, ThumbDownOutlined, ThumbUp, ThumbUpOutlined} from '@mui/icons-material';
import {Checkbox, Chip, styled, TextareaAutosize} from '@mui/material';
import {useCallback, useState} from 'react';
import useTroubleshootService from '../service/useTroubleshootService';
import {FeedbackEnum, GuideData} from '../types/types';
import {useTranslation} from 'react-i18next';
import {useUserInformation} from "thermia-react-container";

const StyledButtons = styled('div')(() => ({
  marginTop: '20px', 

  '.MuiChip-root': {
    marginLeft: 0,
    marginRight: 5,

    '.MuiChip-label': {
      display: 'none'
    },

    '.MuiChip-icon': {
      width: '0.8em',
      height: '0.8em',
      marginLeft: 20,
      marginRight: 20
    }
  }
}));

export default function FeedbackComponent({ guide }: { guide: GuideData }) {
  const { postFeedback, postComment } = useTroubleshootService();
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackEnum>(guide.feedback);
  const [comment, setComment] = useState<string>('');
  const [anonymous, setAnonymous] = useState<boolean>(false);
  const [displayCommentSuccess, setDisplayCommentSuccess] = useState<boolean>(false);
  const { t } = useTranslation('troubleshoot');
  const userInformation = useUserInformation();
  
  const feedbackClick = useCallback(
    async (feedbackClicked: FeedbackEnum) => {
      const newFeedback = selectedFeedback !== feedbackClicked ? feedbackClicked : FeedbackEnum.None;
      setSelectedFeedback(newFeedback); // Optimistic update
      const response = await postFeedback(guide.id, newFeedback);
      setSelectedFeedback(response); // Update with response
    },
    [guide.id, postFeedback, selectedFeedback]
  );

  const sendComment = useCallback(
    async () => {
      const success = await postComment(guide.id, comment, anonymous);
      if(success) {
        setComment('');
        setDisplayCommentSuccess(true);
        setTimeout(() => {
          setDisplayCommentSuccess(false);
        }, 7500)
      }
    },
    [guide.id, postComment, selectedFeedback, comment, anonymous]
  );
  
  return (
    <StyledButtons>
      <div style={{ marginBottom: '5px', paddingTop: '20px', borderTop: 'solid lightgrey 1px' }}>
        {t('troubleshoot.feedback.feedbackHeader')}
      </div>
      <Chip
        color="primary"
        onClick={() => feedbackClick(FeedbackEnum.Positive)}
        icon={selectedFeedback === FeedbackEnum.Positive ? <ThumbUp /> : <ThumbUpOutlined />}
      />
      <Chip
        color="primary"
        onClick={() => feedbackClick(FeedbackEnum.Negative)}
        icon={selectedFeedback === FeedbackEnum.Negative ? <ThumbDown /> : <ThumbDownOutlined />}
      />
      <div style={{ width: '450px' }}>
        <TextareaAutosize
          style={{
            width: '100%', 
            height: '100px',
            marginTop: '20px', 
            padding: '10px',
            borderRadius: '7px',
            border: '1px solid lightgrey',
            fontSize: '16px',
            fontFamily: 'inherit'
          }}
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
          }}
          placeholder={t('troubleshoot.feedback.commentPlaceholder')}>
        </TextareaAutosize>
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <Checkbox
            onChange={(e) => {
              setAnonymous(e.target.checked);
            }}
          ></Checkbox>
          <div style={{ marginTop: '10px' }}>
            {t('troubleshoot.feedback.commentAnonymous')}
            {!anonymous && (
              <div style={{ fontSize: '13px', fontStyle: 'italic' }}>
                {t('troubleshoot.feedback.commentUserInfoDescription')}<br/>
                {t('troubleshoot.feedback.commentUserInfoName', {name: userInformation.name})}<br/>
                {t('troubleshoot.feedback.commentUserInfoEmail', {email: userInformation.email})}<br/>
              </div>
            )}
            {anonymous && (
              <div style={{ fontSize: '13px', fontStyle: 'italic' }}>
                {t('troubleshoot.feedback.commentAnonymousDescription')}
              </div>
            )}
          </div>
        </div>
        <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center' }}>
          <Chip
            color="primary"
            onClick={() => sendComment()}
            icon={<Send />}
          />
          {displayCommentSuccess && (
            <div style={{ color: 'green', fontSize: '16px', marginLeft: '10px' }}>
              {t('troubleshoot.feedback.commentSendSuccess')}
            </div>
          )}
        </div>
      </div>
    </StyledButtons>
  );
}
